/**
 * EditMemberTicketModal - 회원 보유 수강권의 운영 보정 필드를 수정하는 모달 (폼 2-B)
 *
 * - 남은 횟수(remainingCoupon)·총 횟수(maxCoupon)·만료일(expireAt)만 수정한다.
 *   title/type/isActive는 변경하지 않는다.
 * - `useUpdateMemberTicket` 훅으로 Cloud Function `updateMemberTicket` 호출
 * - 성공 시 onUpdated 콜백 → 부모가 모달 close + 수강권 목록 refetch
 *
 * 수강권 식별은 부모가 본 직렬화 배열 순번(ticketIndex)과 title/purchasedAt 가드로 한다.
 */

import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useUpdateMemberTicket, type MemberTicket } from "@/hooks/useTicketPurchase";

interface EditMemberTicketModalProps {
  isOpen: boolean;
  centerId: number;
  memberId: number;
  /** 수정 대상 수강권 (직렬화 뷰) */
  ticket: MemberTicket;
  /** 부모가 본 직렬화 배열에서의 순번 */
  ticketIndex: number;
  onClose: () => void;
  onUpdated: () => void;
}

function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) {
      return msg;
    }
  }
  return null;
}

/** ISO/정규화 문자열 → date input용 로컬 "YYYY-MM-DD". 파싱 실패 시 "". */
function toDateInputValue(value: string | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** date input "YYYY-MM-DD" + 로컬 시각("HH:mm:ss") → ISO. 빈 값이면 null. */
function dateInputToIso(dateStr: string, time: string): string | null {
  if (dateStr === "") return null;
  const d = new Date(`${dateStr}T${time}`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function EditMemberTicketModal({
  isOpen,
  centerId,
  memberId,
  ticket,
  ticketIndex,
  onClose,
  onUpdated,
}: EditMemberTicketModalProps) {
  const [remainingCoupon, setRemainingCoupon] = useState("");
  const [maxCoupon, setMaxCoupon] = useState("");
  const [expireAtDate, setExpireAtDate] = useState("");
  const [purchasedAtDate, setPurchasedAtDate] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const updateMemberTicket = useUpdateMemberTicket();

  // 모달 open 시 대상 수강권 값으로 폼 초기화.
  useEffect(() => {
    if (!isOpen) return;
    setRemainingCoupon(String(ticket.remainingCoupon));
    setMaxCoupon(String(ticket.maxCoupon));
    setExpireAtDate(toDateInputValue(ticket.expireAt));
    setPurchasedAtDate(toDateInputValue(ticket.purchasedAt));
    setValidationError(null);
    updateMemberTicket.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ticket]);

  if (!isOpen) return null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const remaining = Number(remainingCoupon);
    const max = Number(maxCoupon);
    if (!Number.isInteger(max) || max < 1) {
      setValidationError("총 횟수는 1 이상의 정수여야 합니다.");
      return;
    }
    if (!Number.isInteger(remaining) || remaining < 0) {
      setValidationError("남은 횟수는 0 이상의 정수여야 합니다.");
      return;
    }
    if (remaining > max) {
      setValidationError("남은 횟수는 총 횟수를 초과할 수 없습니다.");
      return;
    }
    setValidationError(null);

    updateMemberTicket.mutate(
      {
        centerId,
        memberId,
        ticketIndex,
        expectedTitle: ticket.title,
        expectedPurchasedAt: ticket.purchasedAt,
        remainingCoupon: remaining,
        maxCoupon: max,
        expireAt: dateInputToIso(expireAtDate, "23:59:59"),
        purchasedAt: dateInputToIso(purchasedAtDate, "00:00:00"),
      },
      {
        onSuccess: () => {
          onUpdated();
        },
      },
    );
  };

  const serverErrorMessage = updateMemberTicket.isError
    ? getErrorMessage(updateMemberTicket.error)
    : null;
  const isSubmitDisabled = updateMemberTicket.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">수강권 수정</h2>
        <p className="text-sm text-gray-500 mb-4">
          {ticket.title} · {ticket.type}
        </p>

        <form onSubmit={handleSubmit}>
          {/* 남은 횟수 */}
          <div className="mb-4">
            <label
              htmlFor="remainingCoupon"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              남은 횟수 <span className="text-red-500">*</span>
            </label>
            <input
              id="remainingCoupon"
              aria-label="남은 횟수"
              type="number"
              min="0"
              step="1"
              value={remainingCoupon}
              onChange={(e) => setRemainingCoupon(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* 총 횟수 */}
          <div className="mb-4">
            <label htmlFor="maxCoupon" className="block text-sm font-medium text-gray-700 mb-1">
              총 횟수 <span className="text-red-500">*</span>
            </label>
            <input
              id="maxCoupon"
              aria-label="총 횟수"
              type="number"
              min="1"
              step="1"
              value={maxCoupon}
              onChange={(e) => setMaxCoupon(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* 구매일 (비우면 구매일 없음) */}
          <div className="mb-4">
            <label htmlFor="purchasedAt" className="block text-sm font-medium text-gray-700 mb-1">
              구매일
            </label>
            <input
              id="purchasedAt"
              aria-label="구매일"
              type="date"
              value={purchasedAtDate}
              onChange={(e) => setPurchasedAtDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">비워두면 구매일 없음으로 설정됩니다.</p>
          </div>

          {/* 만료일 (비우면 무제한) */}
          <div className="mb-4">
            <label htmlFor="expireAt" className="block text-sm font-medium text-gray-700 mb-1">
              만료일
            </label>
            <input
              id="expireAt"
              aria-label="만료일"
              type="date"
              value={expireAtDate}
              onChange={(e) => setExpireAtDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">비워두면 무제한으로 설정됩니다.</p>
          </div>

          {/* 검증 / 서버 에러 */}
          {validationError && <p className="text-sm text-red-600 mb-4">{validationError}</p>}
          {updateMemberTicket.isError && !validationError && (
            <p className="text-sm text-red-600 mb-4">
              {serverErrorMessage ?? "수강권 수정에 실패했습니다. 다시 시도해주세요."}
            </p>
          )}

          {/* 버튼 영역 */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMemberTicket.isPending ? "저장 중..." : "저장"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
